import { useRef, MouseEvent, useEffect, useCallback, useState } from 'react';
import { TrackingDataProps } from '../../../common/AnalyticsContext/LinkTracking';
import { applyTrackingId } from '../../../common/AnalyticsContext/applyTrackingId';
import { Popover } from '../../../common/components/Popover/Popover';
import { useBrowser } from '../../../common/services/BrowserService';
import { useMobile } from '../../../common/services/screenSize/ScreenSizeService';
import { safeMemo } from '../../../common/util/safeMemo';
import { DROPDOWN_WIDTH } from '../overlay/Dropdown';
import {
  CrossIcon,
  DropDownIcon,
  Pill,
  StyledFilterIcon,
  AppliedFilterCount,
  ValueCaption,
} from './FilterPill.styles';

const RIGHT_EDGE_DROPDOWN_SPACING = 16;

interface Props extends TrackingDataProps {
  children?: React.ReactNode;
  dropdown?: boolean;
  tooltip?: string;
  onClick?: (x: number, y: number) => void;
  onClear?: VoidFunction;
  /** for when the pill is selected and has no value yet. */
  selected?: boolean;
  /** displays a text value in the pill. acts as has value if caption is given. */
  valueCaption?: string;
  /** for when there is no caption but needs to know if it has a value. */
  hasValue?: boolean;
  initiallyActive?: boolean;
  filterIcon?: boolean;
  className?: string;
  appliedFilterCount?: number;
}

export const FilterPill = safeMemo(
  ({
    selected,
    children,
    dropdown,
    valueCaption,
    tooltip,
    onClick,
    trackingId,
    onClear: onClose,
    hasValue,
    initiallyActive,
    filterIcon,
    className,
    appliedFilterCount,
  }: Props) => {
    const { window } = useBrowser();
    const mobile = useMobile();
    const [initialClick, setInitialClick] = useState<boolean>(initiallyActive ?? false);

    const buttonRef = useRef<HTMLButtonElement>(null);

    const handleClick = useCallback(() => {
      const boundingRect = buttonRef.current ? buttonRef.current.getBoundingClientRect() : null;

      const x = Math.min(
        (window?.innerWidth ?? 0) - DROPDOWN_WIDTH - RIGHT_EDGE_DROPDOWN_SPACING,
        boundingRect?.left ?? 0,
      );

      onClick?.(x, boundingRect?.top ?? 0);
    }, [window?.innerWidth, onClick]);

    useEffect(() => {
      if (initialClick) {
        setInitialClick(false);
        handleClick();
      }
    }, [initialClick, setInitialClick, handleClick]);

    const handleClose = (ev: MouseEvent) => {
      ev.stopPropagation();

      if (onClose) {
        onClose();
      } else {
        handleClick();
      }
    };

    const pill = (
      <Pill
        className={className}
        ref={buttonRef}
        selected={selected}
        hasValue={!!(valueCaption || hasValue)}
        onClick={handleClick}
        {...applyTrackingId(trackingId)}
      >
        {filterIcon && !appliedFilterCount && <StyledFilterIcon />}
        {filterIcon && appliedFilterCount !== undefined && appliedFilterCount > 0 && (
          <AppliedFilterCount>{appliedFilterCount}</AppliedFilterCount>
        )}
        {valueCaption && <ValueCaption>{valueCaption}</ValueCaption>}
        {!valueCaption && children}
        {!!(valueCaption || hasValue) && <CrossIcon onClick={handleClose} />}
        {dropdown && !valueCaption && <DropDownIcon />}
      </Pill>
    );

    if (tooltip && !mobile) {
      return (
        <Popover
          popperOptions={{ modifiers: { preventOverflow: { enabled: false } } }}
          content={tooltip}
        >
          {pill}
        </Popover>
      );
    }

    return pill;
  },
);
